import { CaseReducer, createSlice, PayloadAction } from '@reduxjs/toolkit';
import includes from 'lodash-es/includes';
import get from 'lodash-es/get';
import sortBy from 'lodash-es/sortBy';
import find from 'lodash-es/find';

import {
  SMART_POPUNDER_RESULTS_TABLE_COLUMNS,
  SMART_POPUNDER_RESULTS_TABLE_COLUMNS_IDS,
} from 'common/components/SmartPopunderResults/TableColumns';
import getStatisticsLinkWithGivenParams from 'common/helpers/getStatisticsLinkWithGivenParams';

import { SMART_POPUNDER_ITERATION_STATUSES } from '../constants/smartPopunders';

type IterationType = { ruleId: number; status: string; id: number };

const defaultState = {
  isFetching: false,
  smartpops: [],
  urls: [],
  currentSmartPopunder: null,
  isSmartPopunderFetching: false,
  isIterationsFetching: false,
  iterations: [],
  isRulesFetching: false,
  rules: [],
  isUpdating: false,
  iterationVariationError: null,
  isActionInProgress: false,
  selectedColumnsIds: SMART_POPUNDER_RESULTS_TABLE_COLUMNS_IDS,
  selectedTrafficDetails: {
    trafficType: null,
    trafficDevice: null,
  },
  isPreviousIterationsShown: false,
  isPreviousResultsShown: false,
  isCloningIterationInProgress: false,
  isGlobalIterationsFetching: false,
  globalIterations: [],
};

export type SmartPopundersStateType = {
  isFetching: boolean;
  smartpops: object[];
  urls: string[];
  currentSmartPopunder: null | object;
  iterationVariationError: null | object;
  isSmartPopunderFetching: boolean;
  isIterationsFetching: boolean;
  iterations: IterationType[];
  isRulesFetching: boolean;
  isActionInProgress: boolean;
  rules: object[];
  isUpdating: boolean;
  selectedColumnsIds: string[];
  selectedTrafficDetails: {
    trafficType: null | string;
    trafficDevice: null | string;
  };
  isPreviousIterationsShown: boolean;
  isPreviousResultsShown: boolean;
  isCloningIterationInProgress: boolean;
  isGlobalIterationsFetching: boolean;
  globalIterations: IterationType[];
};
type SmartPopundersReducers = {
  smartPopundersFetchStartAction: CaseReducer<SmartPopundersStateType>;
  smartPopundersFetchSuccessAction: CaseReducer<
    SmartPopundersStateType,
    PayloadAction<{
      smartpops: SmartPopundersStateType['smartpops'];
      urls: SmartPopundersStateType['urls'];
    }>
  >;
  smartPopundersIterationsFetchStartAction: CaseReducer<SmartPopundersStateType>;
  smartPopundersIterationsResetAction: CaseReducer<SmartPopundersStateType>;
  smartPopundersIterationsGlobalFetchStartAction: CaseReducer<SmartPopundersStateType>;
  smartPopundersRulesFetchStartAction: CaseReducer<SmartPopundersStateType>;
  smartPopunderIterationActionStartAction: CaseReducer<SmartPopundersStateType>;
  smartPopunderFetchStartAction: CaseReducer<SmartPopundersStateType>;
  smartPopunderUpdateStartAction: CaseReducer<SmartPopundersStateType>;
  smartPopunderUpdateFailAction: CaseReducer<SmartPopundersStateType>;
  smartPopunderResetIterationVariationErrorAction: CaseReducer<SmartPopundersStateType>;
  smartPopunderIterationActionFailAction: CaseReducer<SmartPopundersStateType>;
  smartPopunderIterationActionSuccessAction: CaseReducer<SmartPopundersStateType>;
  smartPopunderTogglePreviousIterationShowingAction: CaseReducer<SmartPopundersStateType>;
  smartPopunderResetAction: CaseReducer<SmartPopundersStateType>;
  smartPopunderTogglePreviousResultsShowingAction: CaseReducer<SmartPopundersStateType>;
  smartPopunderCloneIterationIdToNewSmartpopSuccessAction: CaseReducer<
    SmartPopundersStateType,
    PayloadAction<SmartPopundersStateType>
  >;
  smartPopunderCloneIterationIdToNewSmartpopStartAction: CaseReducer<SmartPopundersStateType>;
  smartPopundersIterationsFetchSuccessAction: CaseReducer<
    SmartPopundersStateType,
    PayloadAction<SmartPopundersStateType['iterations']>
  >;
  smartPopundersIterationsGlobalFetchSuccessAction: CaseReducer<
    SmartPopundersStateType,
    PayloadAction<SmartPopundersStateType['globalIterations']>
  >;
  smartPopundersSetGlobalRuleAction: CaseReducer<
    SmartPopundersStateType,
    PayloadAction<number>
  >;
  smartPopundersRulesFetchSuccessAction: CaseReducer<
    SmartPopundersStateType,
    PayloadAction<SmartPopundersStateType['rules']>
  >;
  smartPopunderFetchSuccessAction: CaseReducer<
    SmartPopundersStateType,
    PayloadAction<SmartPopundersStateType['currentSmartPopunder']>
  >;
  smartPopunderAddIterationVariationFailAction: CaseReducer<
    SmartPopundersStateType,
    PayloadAction<SmartPopundersStateType['iterationVariationError']>
  >;
  smartPopunderUpdateIterationVariationFailAction: CaseReducer<
    SmartPopundersStateType,
    PayloadAction<SmartPopundersStateType['iterationVariationError']>
  >;
  smartPopunderUpdateSelectedColumnsIdsAction: CaseReducer<
    SmartPopundersStateType,
    PayloadAction<SmartPopundersStateType['selectedColumnsIds']>
  >;
};
type SmartPopundersSelectors = {
  getFilteredIterationsList: (
    state: SmartPopundersStateType,
  ) => SmartPopundersStateType['iterations'];
  getIterationStatisticsLinksMap: (
    state: SmartPopundersStateType,
  ) => Record<number, SmartPopundersStateType['iterations']>;
  getFilteredColumns: (
    state: SmartPopundersStateType,
  ) => typeof SMART_POPUNDER_RESULTS_TABLE_COLUMNS;
  getIterationWhichCouldBeStarted: (
    state: SmartPopundersStateType,
  ) => IterationType | null | undefined;
  getActiveIteration: (
    state: SmartPopundersStateType,
  ) => IterationType | undefined;
  getSelectedTrafficDetails: (
    state: SmartPopundersStateType,
  ) => SmartPopundersStateType['selectedTrafficDetails'];
  getIsPreviousIterationsShown: (
    state: SmartPopundersStateType,
  ) => SmartPopundersStateType['isPreviousIterationsShown'];
  getIsPreviousResultsShown: (
    state: SmartPopundersStateType,
  ) => SmartPopundersStateType['isPreviousResultsShown'];
};

const smartPopunders = createSlice<
  SmartPopundersStateType,
  SmartPopundersReducers,
  'smartPopunders',
  SmartPopundersSelectors
>({
  initialState: defaultState,
  name: 'smartPopunders',
  reducers: {
    smartPopundersFetchStartAction: (state) => ({ ...state, isFetching: true }),
    smartPopundersFetchSuccessAction: (state, { payload }) => ({
      ...state,
      isFetching: false,
      smartpops: payload.smartpops,
      urls: payload.urls,
    }),
    smartPopundersIterationsFetchStartAction: (state) => ({
      ...state,
      isIterationsFetching: true,
    }),
    smartPopundersIterationsFetchSuccessAction: (state, { payload }) => ({
      ...state,
      isIterationsFetching: false,
      iterations: payload,
    }),
    smartPopundersIterationsResetAction: (state) => ({
      ...state,
      isIterationsFetching: false,
      iterations: [],
    }),
    smartPopundersIterationsGlobalFetchStartAction: (state) => ({
      ...state,
      isGlobalIterationsFetching: true,
    }),
    smartPopundersIterationsGlobalFetchSuccessAction: (state, { payload }) => ({
      ...state,
      isGlobalIterationsFetching: false,
      globalIterations: payload,
    }),
    smartPopundersSetGlobalRuleAction: (state, { payload }) => ({
      ...state,
      iterations: state.globalIterations.filter(
        (iteration) => iteration.ruleId === payload,
      ),
    }),
    smartPopundersRulesFetchStartAction: (state) => ({
      ...state,
      isRulesFetching: true,
    }),
    smartPopundersRulesFetchSuccessAction: (state, { payload }) => ({
      ...state,
      isRulesFetching: false,
      rules: payload,
    }),
    smartPopunderFetchStartAction: (state) => ({
      ...state,
      isSmartPopunderFetching: true,
    }),
    smartPopunderResetAction: (state) => ({
      ...state,
      currentSmartPopunder: null,
      iterations: [],
      rules: [],
    }),
    smartPopunderFetchSuccessAction: (state, { payload }) => ({
      ...state,
      currentSmartPopunder: payload,
      isSmartPopunderFetching: false,
      isUpdating: false,
    }),
    smartPopunderUpdateStartAction: (state) => ({
      ...state,
      isUpdating: true,
    }),
    smartPopunderUpdateFailAction: (state) => ({
      ...state,
      isUpdating: false,
    }),
    smartPopunderAddIterationVariationFailAction: (state, { payload }) => ({
      ...state,
      iterationVariationError: payload,
    }),
    smartPopunderUpdateIterationVariationFailAction: (state, { payload }) => ({
      ...state,
      iterationVariationError: payload,
    }),
    smartPopunderResetIterationVariationErrorAction: (state) => ({
      ...state,
      iterationVariationError: null,
    }),
    smartPopunderIterationActionStartAction: (state) => ({
      ...state,
      isActionInProgress: true,
    }),
    smartPopunderIterationActionFailAction: (state) => ({
      ...state,
      isActionInProgress: false,
      isCloningIterationInProgress: false,
    }),
    smartPopunderIterationActionSuccessAction: (state) => ({
      ...state,
      isActionInProgress: false,
      isCloningIterationInProgress: false,
    }),
    smartPopunderUpdateSelectedColumnsIdsAction: (state, { payload }) => ({
      ...state,
      selectedColumnsIds: [...new Set(['id', ...payload])],
    }),
    smartPopunderTogglePreviousIterationShowingAction: (state) => ({
      ...state,
      isPreviousIterationsShown: !state.isPreviousIterationsShown,
    }),
    smartPopunderTogglePreviousResultsShowingAction: (state) => ({
      ...state,
      isPreviousResultsShown: !state.isPreviousResultsShown,
    }),
    smartPopunderCloneIterationIdToNewSmartpopStartAction: (state) => ({
      ...state,
      isCloningIterationInProgress: true,
    }),
    smartPopunderCloneIterationIdToNewSmartpopSuccessAction: (state) => ({
      ...state,
      isCloningIterationInProgress: false,
    }),
  },
  selectors: {
    getFilteredIterationsList: ({
      isPreviousIterationsShown = false,
      iterations = [],
    }) =>
      iterations.filter((item) => {
        if (!isPreviousIterationsShown) {
          return includes(
            [
              SMART_POPUNDER_ITERATION_STATUSES.STOPPED,
              SMART_POPUNDER_ITERATION_STATUSES.STARTED,
            ],
            item.status,
          );
        }

        return true;
      }),
    getIterationStatisticsLinksMap: ({ iterations }) =>
      iterations.reduce((result, iteration) => {
        const statisticsParams = get(iteration, 'results.statisticsParams');

        if (!statisticsParams) {
          return result;
        }

        return {
          ...result,
          [iteration.id]: getStatisticsLinkWithGivenParams(statisticsParams),
        };
      }, {}),
    getFilteredColumns: ({ selectedColumnsIds }) =>
      // Apply sorting based on selecting in control
      sortBy(
        SMART_POPUNDER_RESULTS_TABLE_COLUMNS.filter((column) =>
          includes(selectedColumnsIds, column.id),
        ),
        (column) => selectedColumnsIds.indexOf(column.id),
      ),
    getIterationWhichCouldBeStarted: ({ iterations = [] }) => {
      const isStartedAny = find(iterations, {
        status: SMART_POPUNDER_ITERATION_STATUSES.STARTED,
      });

      if (isStartedAny) {
        return null;
      }

      return find(
        sortBy(iterations, ['id']),
        (iteration) =>
          iteration.status === SMART_POPUNDER_ITERATION_STATUSES.STOPPED,
      );
    },
    getActiveIteration: ({ iterations }) =>
      find(iterations, {
        status: SMART_POPUNDER_ITERATION_STATUSES.STARTED,
      }),
    getSelectedTrafficDetails: ({ selectedTrafficDetails }) =>
      selectedTrafficDetails,
    getIsPreviousIterationsShown: ({ isPreviousIterationsShown }) =>
      isPreviousIterationsShown,
    getIsPreviousResultsShown: ({ isPreviousResultsShown }) =>
      isPreviousResultsShown,
  },
});

export const {
  smartPopundersFetchStartAction,
  smartPopundersFetchSuccessAction,
  smartPopundersIterationsFetchStartAction,
  smartPopundersIterationsFetchSuccessAction,
  smartPopundersIterationsResetAction,
  smartPopundersIterationsGlobalFetchStartAction,
  smartPopundersIterationsGlobalFetchSuccessAction,
  smartPopundersSetGlobalRuleAction,
  smartPopundersRulesFetchStartAction,
  smartPopundersRulesFetchSuccessAction,
  smartPopunderFetchStartAction,
  smartPopunderFetchSuccessAction,
  smartPopunderResetAction,
  smartPopunderUpdateStartAction,
  smartPopunderUpdateFailAction,
  smartPopunderAddIterationVariationFailAction,
  smartPopunderUpdateIterationVariationFailAction,
  smartPopunderResetIterationVariationErrorAction,
  smartPopunderIterationActionStartAction,
  smartPopunderIterationActionFailAction,
  smartPopunderIterationActionSuccessAction,
  smartPopunderUpdateSelectedColumnsIdsAction,
  smartPopunderTogglePreviousIterationShowingAction,
  smartPopunderTogglePreviousResultsShowingAction,
  smartPopunderCloneIterationIdToNewSmartpopStartAction,
  smartPopunderCloneIterationIdToNewSmartpopSuccessAction,
} = smartPopunders.actions;

export const {
  getFilteredIterationsList,
  getIterationStatisticsLinksMap,
  getFilteredColumns,
  getIterationWhichCouldBeStarted,
  getActiveIteration,
  getSelectedTrafficDetails,
  getIsPreviousIterationsShown,
  getIsPreviousResultsShown,
} = smartPopunders.selectors;

export default smartPopunders.reducer;
